import {createSocket, type RemoteInfo, Socket} from 'node:dgram';
import {type DiscoveryNode, type DiscoveryStrategy} from '../index.ts';
import {SimpleDiscoveryNode} from '../SimpleDiscoveryNode.ts';
import {type Properties} from '../../config/Properties.ts';
import {GROUP, PORT} from './MulticastProperties.ts';
import {getOrDefault} from '../settings.ts';
import {fromHostname, toIpAddress} from '../../cluster/Address.ts';
import {isLoopback} from '../../utils/network.ts';
import {type Logger} from '../../logging.ts';

const DATA_OUTPUT_BUFFER_SIZE = 64 * 1024;
const DEFAULT_MULTICAST_PORT = 34567;
const DEFAULT_MULTICAST_GROUP = '239.1.2.3';
const SOCKET_TIME_TO_LIVE = 255; // unrestricted (other possible values would be 1 (local-network), 15 (site-local), 32(local-area), 64(regional), 128(global))

interface MulticastMemberInfo {
    readonly host: string;
    readonly port: number;
}

function multicastDiscoverySender(discoveryNode: DiscoveryNode | undefined, socket: Socket, group: string, port: number): () => void {
    let multicastMemberInfo: MulticastMemberInfo;
    if (discoveryNode) {
        const address = discoveryNode.publicAddress;
        multicastMemberInfo = {host: address.host, port: address.port};
    }
    const msg: Buffer = Buffer.from(JSON.stringify(multicastMemberInfo));

    return () => {
        socket.send(msg, port, group, (error) => {
            console.error(error);
        });
    }
}

function multicastDiscoveryReceiver(socket: Socket): () => Promise<MulticastMemberInfo> {
    return () => {
        return new Promise<MulticastMemberInfo>((resolve, reject) => {
            socket.on('message', (message: Buffer, remote: RemoteInfo) => {
                resolve(JSON.parse(message as unknown as string));
            });
            socket.on('error', (err: Error) => {
                reject(err);
            });
        });
    }
}

export class MulticastDiscoveryStrategy implements DiscoveryStrategy {
    private readonly logger: Logger;
    private readonly properties: Properties;
    private readonly discoveryNode?: DiscoveryNode;
    private isClient?: boolean;
    private discoverySender?: () => void;
    private discoveryReceiver?: () => Promise<MulticastMemberInfo>;
    private sendIntervalId?: ReturnType<typeof setInterval>;
    private socket?: Socket;
    constructor(discoveryNode: DiscoveryNode | undefined,
                logger: Logger,
                properties: Properties) {
        this.logger = logger;
        this.properties = properties;
        this.discoveryNode = discoveryNode;
    }

    private async initializeMulticastSocket() {
        const port = getOrDefault(this.properties, '', PORT, DEFAULT_MULTICAST_PORT);
        if (port < 0 || port > 65535) {
            throw new Error('port number must be between 0 and 65535');
        }
        const group = getOrDefault(this.properties, '', GROUP, DEFAULT_MULTICAST_GROUP);
        this.socket = createSocket({
            type: 'udp4',
            reuseAddr: true,
            recvBufferSize: DATA_OUTPUT_BUFFER_SIZE,
            sendBufferSize: DATA_OUTPUT_BUFFER_SIZE});

        if (this.discoveryNode) {
            const address = await toIpAddress(this.discoveryNode.privateAddress);
            if (!isLoopback(address)) {
                this.socket.setMulticastInterface(address.address);
            }
        }

        this.socket.bind(port, () => {
            this.socket.setMulticastTTL(SOCKET_TIME_TO_LIVE);
            this.socket.addMembership(group);
        });

        this.discoverySender = multicastDiscoverySender(this.discoveryNode, this.socket, group, port);
        this.discoveryReceiver = multicastDiscoveryReceiver(this.socket);
        if (this.discoveryNode == null) {
            this.isClient = true;
        }
    }

    async discover(): Promise<Iterable<DiscoveryNode> | undefined> {
        const multicastMemberInfo = await this.discoveryReceiver();
        if (multicastMemberInfo) {
            const list = [];
            try {
                const discoveryNode = new SimpleDiscoveryNode(await fromHostname(multicastMemberInfo.host, multicastMemberInfo.port));
                list.push(discoveryNode);
            }
            catch (e) {
                this.logger.trace(e.message);
            }
            return list;
        }
    }

    async start() {
        await this.initializeMulticastSocket();
        if (!this.isClient) {
            this.sendIntervalId = setInterval(() => {
                this.discoverySender();
            }, 2000);
        }
    }

    async close() {
        if (this.sendIntervalId) {
            clearInterval(this.sendIntervalId);
            delete this.sendIntervalId;
        }
        return new Promise<void>((resolve, reject) => {
            if (this.socket)
            {
                this.socket.close(() => {
                    resolve()
                });
            }
            else
            {
                resolve();
            }
        });

    }

}
